// Copyright (c) StrangeBee 2023

import { IconLabel } from '@Atoms/Info';
import OrganisationAvatar from '@Atoms/UserAvatar/UserAvatar';
import { DefaultOptionType, SelectProps, SelectValue } from 'antd/lib/select';
import React, { ReactElement, useMemo } from 'react';
import { OrganisationLinksOutput } from 'thehive-sdk';
import { THBaseSelector } from '@Molecules/Inputs/THBaseSelector/THBaseSelector';

interface THOrganisationSelectorProps<T extends SelectValue> extends SelectProps<T> {
    organisations?: OrganisationLinksOutput[];
    borderless?: boolean;
}

export const THOrganisationSelector = <T extends SelectValue>({
    organisations = [],
    className,
    allowClear = true,
    borderless = true,
    ...props
}: THOrganisationSelectorProps<T>): ReactElement => {
    const organisationsSorted = useMemo<DefaultOptionType[]>(
        () =>
            organisations
                ?.sort(({ toOrganisation: nameA }, { toOrganisation: nameB }) => nameA?.localeCompare(nameB || '') || 0)
                .map(({ toOrganisation, avatar }) => ({
                    label: (
                        <IconLabel
                            key={toOrganisation}
                            icon={<OrganisationAvatar name={toOrganisation} avatar={avatar} />}
                            label={toOrganisation}
                        />
                    ),
                    value: toOrganisation,
                })),
        [organisations],
    );

    return (
        <THBaseSelector
            allowClear={allowClear}
            borderless={borderless}
            className={className}
            options={organisationsSorted}
            {...props}
        />
    );
};
